<template>
  <!-- Сроки задаются отдельно для каждой используемой формы обучения. -->
  <v-card class="admission-calendar" elevation="0">
    <v-card-title class="admission-calendar__header">
      <div>
        <h2 class="admission-calendar__title">Календарь приёмной комиссии</h2>
        <p class="admission-calendar__description">
          Настройте сроки приёмной кампании отдельно для каждой формы обучения.
        </p>
      </div>
    </v-card-title>

    <v-card-text class="admission-calendar__content">
      <v-alert
        v-if="error"
        class="admission-calendar__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading" class="admission-calendar__loading">
        <v-skeleton-loader v-for="index in 3" :key="index" type="article, actions" />
      </div>

      <v-alert v-else-if="!items.length" type="info" variant="tonal">
        Формы обучения для специальностей ещё не настроены.
      </v-alert>

      <div v-else class="admission-calendar__list">
        <section
          v-for="item in items"
          :key="item.id"
          class="admission-calendar__item"
        >
          <div class="admission-calendar__item-header">
            <div class="admission-calendar__item-icon">
              <v-icon icon="mdi-calendar-month-outline" size="24" />
            </div>
            <div>
              <h3 class="admission-calendar__item-title">{{ item.name }}</h3>
              <span class="admission-calendar__item-caption">Форма обучения</span>
            </div>
          </div>

          <v-alert
            v-if="item.error"
            class="admission-calendar__item-alert"
            type="error"
            variant="tonal"
            density="compact"
          >
            {{ item.error }}
          </v-alert>

          <div class="admission-calendar__fields">
            <v-text-field
              v-model="item.startDate"
              type="date"
              label="Дата начала"
              hint="Начало приёма заявлений"
              persistent-hint
              variant="outlined"
              clearable
              append-inner-icon="mdi-calendar-month-outline"
              :disabled="item.saving"
              @update:model-value="markChanged(item)"
              @click:append-inner="openDatePicker"
            />

            <v-text-field
              v-model="item.documentsEndDate"
              type="date"
              label="Окончание приёма документов"
              hint="Последний день подачи документов"
              persistent-hint
              variant="outlined"
              clearable
              append-inner-icon="mdi-calendar-month-outline"
              :disabled="item.saving"
              @update:model-value="markChanged(item)"
              @click:append-inner="openDatePicker"
            />

            <v-text-field
              v-model="item.enrollmentDate"
              type="date"
              label="Дата зачисления"
              hint="Дата завершения приёмной кампании"
              persistent-hint
              variant="outlined"
              clearable
              append-inner-icon="mdi-calendar-month-outline"
              :disabled="item.saving"
              @update:model-value="markChanged(item)"
              @click:append-inner="openDatePicker"
            />
          </div>

          <div class="admission-calendar__item-footer">
            <v-fade-transition>
              <span v-if="item.saved" class="admission-calendar__saved">
                <v-icon icon="mdi-check-circle-outline" size="18" />
                Даты сохранены
              </span>
            </v-fade-transition>

            <v-btn
              color="primary"
              variant="flat"
              prepend-icon="mdi-content-save-outline"
              :loading="item.saving"
              :disabled="!item.changed"
              @click="saveItem(item)"
            >
              Сохранить
            </v-btn>
          </div>
        </section>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'CalendarSection',
  data() {
    return {
      items: [],
      loading: true,
      error: ''
    };
  },
  created() {
    this.loadCalendar();
  },
  methods: {
    loadCalendar() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getAdmissionCalendar'
        )
        .then((response) => {
          this.items = (response.data.items || []).map((item) => ({
            ...item,
            saving: false,
            changed: false,
            saved: false,
            error: ''
          }));
        })
        .catch((error) => {
          this.error = 'Не удалось загрузить календарь приёмной комиссии.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    markChanged(item) {
      item.changed = true;
      item.saved = false;
      item.error = '';
    },
    openDatePicker(event) {
      const target = event ? (event.currentTarget || event.target) : null;
      const field = target ? target.closest('.v-field') : null;
      const input = field ? field.querySelector('input[type="date"]') : null;

      if (!input) {
        return;
      }

      if (typeof input.showPicker === 'function') {
        input.showPicker();
      } else {
        input.focus();
        input.click();
      }
    },
    validateItem(item) {
      // Частично заполненный календарь допустим, но все указанные даты
      // должны следовать в порядке «начало → документы → зачисление».
      if (
        item.startDate &&
        item.documentsEndDate &&
        item.startDate > item.documentsEndDate
      ) {
        return 'Дата начала не может быть позже окончания приёма документов.';
      }

      if (
        item.documentsEndDate &&
        item.enrollmentDate &&
        item.documentsEndDate > item.enrollmentDate
      ) {
        return 'Дата зачисления не может быть раньше окончания приёма документов.';
      }

      if (item.startDate && item.enrollmentDate && item.startDate > item.enrollmentDate) {
        return 'Дата зачисления не может быть раньше даты начала.';
      }

      return '';
    },
    saveItem(item) {
      item.error = this.validateItem(item);
      if (item.error) {
        return;
      }

      item.saving = true;
      item.saved = false;

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveAdmissionCalendar',
          new URLSearchParams({
            studyId: String(item.id),
            startDate: item.startDate || '',
            documentsEndDate: item.documentsEndDate || '',
            enrollmentDate: item.enrollmentDate || ''
          })
        )
        .then((response) => {
          const savedItem = response.data.item;
          item.startDate = savedItem.startDate;
          item.documentsEndDate = savedItem.documentsEndDate;
          item.enrollmentDate = savedItem.enrollmentDate;
          item.changed = false;
          item.saved = true;
        })
        .catch((error) => {
          item.error = 'Не удалось сохранить календарь для этой формы обучения.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          item.saving = false;
        });
    }
  }
};
</script>

<style scoped>
.admission-calendar {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.admission-calendar__header {
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.admission-calendar__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.admission-calendar__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.admission-calendar__content {
  padding: 24px 28px 28px;
}

.admission-calendar__alert {
  margin-bottom: 20px;
}

.admission-calendar__loading,
.admission-calendar__list {
  display: grid;
  gap: 20px;
}

.admission-calendar__item {
  padding: 20px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}

.admission-calendar__item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.admission-calendar__item-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 10px;
  background: #eaf3fc;
  color: #1565c0;
}

.admission-calendar__item-title {
  margin: 0;
  color: #344054;
  font-size: 18px;
  font-weight: 600;
}

.admission-calendar__item-caption {
  color: #667085;
  font-size: 13px;
}

.admission-calendar__item-alert {
  margin-bottom: 18px;
}

.admission-calendar__fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.admission-calendar__fields :deep(input[type='date']) {
  width: 100%;
  min-width: 0;
  padding-right: 0;
}

.admission-calendar__fields :deep(input[type='date']::-webkit-calendar-picker-indicator) {
  display: none;
  -webkit-appearance: none;
}

.admission-calendar__item-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  min-height: 36px;
  margin-top: 18px;
}

.admission-calendar__saved {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #2e7d32;
  font-size: 14px;
}

@media (max-width: 1050px) {
  .admission-calendar__fields {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .admission-calendar__header,
  .admission-calendar__content {
    padding-right: 18px;
    padding-left: 18px;
  }

  .admission-calendar__item {
    padding: 16px;
  }
}
</style>
